import type { PermissionString, RawGrant } from './types';

/**
 * Step 2 of the engine (Addendum A.2): dotted grants ∩ typed catalog → concrete permissions.
 *
 * Wildcards are resolved exactly once per login against the catalog aggregated from every module
 * manifest, so every later check is an O(1) `Set.has`. Grants that match nothing in the catalog
 * (backend-only or legacy-only features) expand to nothing — harmless by construction, and still
 * visible in the inspector.
 */

const segmentMatches = (pattern: string | undefined, value: string): boolean =>
  pattern === '*' || pattern === value;

/**
 * A two-segment grant is a domain grant: `policy-management.*` covers every entity and action.
 * A three-segment grant matches segment-wise, so `eis.*.view` and `policy-management.group-policies.*`
 * both work — the two shapes production roles actually use.
 */
export function grantMatches(grant: RawGrant, permission: PermissionString): boolean {
  const g = grant.split('.');
  const [domain, entity, action] = permission.split('.');
  if (domain === undefined || entity === undefined || action === undefined) return false;

  if (g.length === 2) {
    // `*.*` is the superuser form; `domain.*` covers a whole domain.
    return segmentMatches(g[0], domain) && (g[1] === '*' || g[1] === entity);
  }
  if (g.length === 3) {
    return (
      segmentMatches(g[0], domain) && segmentMatches(g[1], entity) && segmentMatches(g[2], action)
    );
  }
  return false;
}

export function expandGrants(
  grants: readonly RawGrant[],
  catalog: readonly PermissionString[],
): Set<PermissionString> {
  const effective = new Set<PermissionString>();
  for (const permission of catalog) {
    for (const grant of grants) {
      if (grantMatches(grant, permission)) {
        effective.add(permission);
        break;
      }
    }
  }
  return effective;
}
